
import React, { useState } from "react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  MessageSquareIcon, 
  LifeBuoyIcon, 
  BookIcon, 
  PhoneIcon, 
  SendIcon, 
  CheckCircle2Icon, 
  ChevronDownIcon, 
  SearchIcon,
  Mail,
  HelpCircle,
  FileTextIcon
} from "lucide-react";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from "sonner";

const SupportCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      toast.info(`Searching for: ${searchQuery}`);
    }
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (contactEmail && contactMessage) {
      toast.success("Support request submitted successfully!");
      setContactEmail("");
      setContactMessage("");
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  return (
    <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8 max-w-7xl">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-2xl p-8 md:p-12 mb-12 relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-400 bg-clip-text text-transparent mb-4">
            Support Center
          </h1>
          <p className="text-gray-700 text-lg max-w-3xl mb-6">
            Welcome to the DGK Freelance Hub Support Center. Find answers to common questions, 
            browse our documentation, or get in touch with our support team.
          </p>
          
          <form onSubmit={handleSearch} className="relative max-w-xl mb-2">
            <Input
              className="pl-10 bg-white/90 border-0 shadow-md h-12"
              placeholder="Search for help articles, guides, or FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Button 
              type="submit" 
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-10"
            >
              Search
            </Button>
          </form>
        </div>
        
        {/* Abstract decorative elements */}
        <div className="absolute right-0 bottom-0 w-64 h-64 bg-blue-200 rounded-full opacity-30 -mr-20 -mb-20"></div>
        <div className="absolute right-20 top-20 w-20 h-20 bg-purple-200 rounded-full opacity-40"></div>
        <div className="absolute left-10 bottom-10 w-40 h-40 bg-cyan-200 rounded-full opacity-30"></div>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="faq" className="mb-12">
        <TabsList className="grid grid-cols-4 w-full max-w-3xl mx-auto mb-10">
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            <span className="hidden sm:inline">FAQs</span>
          </TabsTrigger>
          <TabsTrigger value="guides" className="flex items-center gap-2">
            <BookIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Guides</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <MessageSquareIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Contact Us</span>
          </TabsTrigger>
          <TabsTrigger value="support-plans" className="flex items-center gap-2">
            <LifeBuoyIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Support Plans</span>
          </TabsTrigger>
        </TabsList>

        {/* FAQs Tab */}
        <TabsContent value="faq">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Frequently Asked Questions</h2>
            
            <Card className="border-0 shadow-md mb-8">
              <CardHeader>
                <CardTitle className="text-lg text-gray-800">Account & Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-gray-700">How do I create an account?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      To create an account, click on the "Sign Up" button in the top right corner of the homepage. 
                      Fill out the registration form with your details and follow the instructions to verify your email address.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2">
                    <AccordionTrigger className="text-gray-700">How do I reset my password?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      To reset your password, click on the "Log In" button, then select "Forgot Password." 
                      Enter your email address, and we'll send you a link to reset your password.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3">
                    <AccordionTrigger className="text-gray-700">How do I update my profile information?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      Log in to your account, navigate to the "Profile" page, and click on the "Edit Profile" button. 
                      From there, you can update your information, skills, and upload a profile picture.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md mb-8">
              <CardHeader>
                <CardTitle className="text-lg text-gray-800">Projects & Contracts</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-gray-700">How do I post a new project?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      Navigate to the "Submit Project" page from your dashboard. Fill out the project details form, 
                      including title, description, budget, timeline, and required skills. Review the information and submit.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2">
                    <AccordionTrigger className="text-gray-700">How do smart contracts work on the platform?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      Our smart contracts are blockchain-based agreements that automatically execute when 
                      predefined conditions are met. They handle payment escrow, milestone releases, and dispute resolution,
                      providing security and transparency for both clients and freelancers.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3">
                    <AccordionTrigger className="text-gray-700">What happens if there's a dispute with a project?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      If a dispute arises, you can use our Dispute Resolution Center accessible from the project page. 
                      Our mediation team will review the case and work with both parties to find a fair resolution 
                      based on contract terms and project history.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md mb-8">
              <CardHeader>
                <CardTitle className="text-lg text-gray-800">Payments & Wallet</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-gray-700">What payment methods do you accept?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      We accept major credit cards, PayPal, and several cryptocurrencies including Bitcoin, 
                      Ethereum, and our native DGK token. Payment methods can be managed in your Wallet settings.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2">
                    <AccordionTrigger className="text-gray-700">How do I withdraw funds from my wallet?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      To withdraw funds, go to your Wallet page, click on "Withdraw," select your preferred 
                      withdrawal method, enter the amount, and confirm the transaction. Processing times vary 
                      depending on the withdrawal method.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-3">
                    <AccordionTrigger className="text-gray-700">What are the platform fees?</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      Our platform charges a 5% fee on all transactions. Premium members enjoy reduced fees of 3%. 
                      There are no fees for depositing funds, but withdrawal fees may apply depending on the 
                      payment method.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Guides Tab */}
        <TabsContent value="guides">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Support Guides</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-t-lg">
                  <CardTitle className="text-gray-800">Getting Started Guide</CardTitle>
                  <CardDescription>Complete walkthrough for new users</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">Learn how to set up your profile, browse projects, submit proposals, and more.</p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Account setup and verification
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Creating an effective profile
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Finding your first project
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">View Guide</Button>
                </CardFooter>
              </Card>
              
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-t-lg">
                  <CardTitle className="text-gray-800">Blockchain & Smart Contracts</CardTitle>
                  <CardDescription>Understanding blockchain features</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">Learn how our blockchain technology secures your work and payments.</p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Smart contract basics
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Cryptocurrency payments
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Escrow system and milestones
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">View Guide</Button>
                </CardFooter>
              </Card>
              
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-t-lg">
                  <CardTitle className="text-gray-800">Freelancer Success Guide</CardTitle>
                  <CardDescription>Tips to maximize your earnings</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">Learn strategies to stand out, win more projects, and increase your income.</p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Crafting winning proposals
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Setting optimal rates
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Building your reputation
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">View Guide</Button>
                </CardFooter>
              </Card>
              
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-t-lg">
                  <CardTitle className="text-gray-800">Client Hiring Guide</CardTitle>
                  <CardDescription>Best practices for hiring</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">Learn how to find, vet, and work effectively with freelance talent.</p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Writing effective project briefs
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Evaluating proposals and portfolios
                    </li>
                    <li className="flex items-center">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500 mr-2" />
                      Managing remote freelancers
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">View Guide</Button>
                </CardFooter>
              </Card>
            </div>
            
            <div className="text-center mt-8">
              <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-300">
                <FileTextIcon className="h-4 w-4 mr-2" />
                Browse All Guides
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Contact Us Tab */}
        <TabsContent value="contact">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Contact Our Support Team</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="border-0 shadow-md text-center py-6">
                <CardContent>
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <MessageSquareIcon className="h-8 w-8 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Live Chat</h3>
                  <p className="text-gray-600 mb-4">Chat with our support team in real-time.</p>
                  <p className="text-gray-500 text-sm">Available 24/7</p>
                </CardContent>
                <CardFooter className="flex justify-center pt-0">
                  <Button>Start Chat</Button>
                </CardFooter>
              </Card>
              
              <Card className="border-0 shadow-md text-center py-6">
                <CardContent>
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                      <Mail className="h-8 w-8 text-purple-600" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Email Support</h3>
                  <p className="text-gray-600 mb-4">Send us a message and we'll respond ASAP.</p>
                  <p className="text-gray-500 text-sm">Response within 24 hours</p>
                </CardContent>
                <CardFooter className="flex justify-center pt-0">
                  <Button>Email Us</Button>
                </CardFooter>
              </Card>
              
              <Card className="border-0 shadow-md text-center py-6">
                <CardContent>
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <PhoneIcon className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Phone Support</h3>
                  <p className="text-gray-600 mb-4">Call us directly for urgent assistance.</p>
                  <p className="text-gray-500 text-sm">Mon-Fri, 9 AM - 5 PM EST</p>
                </CardContent>
                <CardFooter className="flex justify-center pt-0">
                  <Button>+1 (555) 123-4567</Button>
                </CardFooter>
              </Card>
            </div>
            
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>Send Us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <Input id="name" placeholder="Your name" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="Your email address" 
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <Input id="subject" placeholder="What is your inquiry about?" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea 
                      id="message" 
                      rows={5} 
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Please describe your issue or question in detail..."
                      value={contactMessage}
                      onChange={(e) => setContactMessage(e.target.value)}
                      required
                    ></textarea>
                  </div>
                  <div>
                    <Button type="submit" className="w-full md:w-auto">
                      <SendIcon className="h-4 w-4 mr-2" />
                      Submit Request
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Support Plans Tab */}
        <TabsContent value="support-plans">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Support Plans</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-md relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-1 rounded-bl-lg">
                  Free
                </div>
                <CardHeader className="border-b pb-6 pt-10">
                  <CardTitle className="text-xl text-center">Basic Support</CardTitle>
                  <div className="text-center mt-4">
                    <span className="text-3xl font-bold">$0</span>
                    <span className="text-gray-500 text-sm">/month</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Community forum access</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Knowledge base articles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Email support (48h response)</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">Current Plan</Button>
                </CardFooter>
              </Card>
              
              <Card className="border-0 shadow-lg relative overflow-hidden scale-105 bg-gradient-to-br from-slate-50 to-blue-50 z-10">
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                  Popular
                </div>
                <CardHeader className="border-b pb-6 pt-10">
                  <CardTitle className="text-xl text-center">Professional Support</CardTitle>
                  <div className="text-center mt-4">
                    <span className="text-3xl font-bold">$29</span>
                    <span className="text-gray-500 text-sm">/month</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Everything in Basic</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Priority email support (24h)</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Live chat support</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Phone support (business hours)</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-300">
                    Upgrade
                  </Button>
                </CardFooter>
              </Card>
              
              <Card className="border-0 shadow-md relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                  Enterprise
                </div>
                <CardHeader className="border-b pb-6 pt-10">
                  <CardTitle className="text-xl text-center">Premium Support</CardTitle>
                  <div className="text-center mt-4">
                    <span className="text-3xl font-bold">$99</span>
                    <span className="text-gray-500 text-sm">/month</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Everything in Professional</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">24/7 priority support</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Dedicated account manager</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Custom contract templates</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2Icon className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span className="text-gray-600">Quarterly strategy reviews</span>
                    </li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">Contact Sales</Button>
                </CardFooter>
              </Card>
            </div>
            
            <div className="text-center mt-10 text-gray-600">
              <p className="mb-2">Need a custom support solution for your enterprise?</p>
              <Button variant="link" className="text-blue-600 hover:text-blue-700">
                Contact our enterprise sales team
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Testimonials */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-8 mb-12">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">What Our Users Say</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-semibold">JD</span>
                </div>
                <div>
                  <h4 className="font-semibold">John Doe</h4>
                  <p className="text-sm text-gray-500">Freelance Developer</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "The support team helped me resolve a payment issue within minutes. Their quick
                response saved me from missing a project deadline. Excellent service!"
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                  <span className="text-purple-600 font-semibold">AS</span>
                </div>
                <div>
                  <h4 className="font-semibold">Alice Smith</h4>
                  <p className="text-sm text-gray-500">Project Manager</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "As a client hiring multiple freelancers, I had questions about team management.
                The support guides were comprehensive and the live chat was extremely helpful."
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <span className="text-green-600 font-semibold">RJ</span>
                </div>
                <div>
                  <h4 className="font-semibold">Robert Johnson</h4>
                  <p className="text-sm text-gray-500">Web Designer</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "The knowledge base articles helped me understand how the smart contract system
                works. When I needed more clarification, support was just a click away."
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Bottom CTA */}
      <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0 shadow-xl mb-8">
        <CardContent className="pt-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-2">Still need help?</h3>
              <p className="opacity-90 mb-6">
                Our support team is always ready to assist you with any questions or issues you may have.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100">
                  <MessageSquareIcon className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
                <Button variant="outline" className="text-white border-white hover:bg-white/10">
                  Browse FAQs
                </Button>
              </div>
            </div>
            <div className="hidden md:flex justify-end">
              <LifeBuoyIcon className="h-28 w-28 text-white/20" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-center mt-12 text-sm text-gray-500">
        <p>© {new Date().getFullYear()} DGK Freelance Hub. All rights reserved.</p>
      </div>
    </div>
  );
};

export default SupportCenter;
